import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import { ChevronRight, MessageSquare, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'
import { ExtractionTable } from '@/components/extraction/ExtractionTable'
import { CriterionDrawer } from '@/components/extraction/CriterionDrawer'
import { RowActionsMenu } from '@/components/extraction/RowActionsMenu'
import { AddItemModal, type ModalFieldConfig } from '@/components/extraction/AddItemModal'
import { BulkActionsBar } from '@/components/ui/bulk-actions-bar'
import { BulkConfirmDelete } from '@/components/extraction/BulkConfirmDelete'
import { Button } from '@/components/ui/button'
import { useCommentsStore } from '@/stores/comments-store'
import { useExtractionStore } from '@/stores/extraction-store'
import type { ExtractedCriterion } from '@/types/extraction'
import type { SourceHighlightData } from '@/stores/assistant-store'
import { addItemWithFeedback } from '@/lib/add-item-feedback'
import { summariseCriteriaWeights } from '@/lib/criteria-weights'
import { normalizeTitle } from '@/lib/criterion-title'
import { cn } from '@/lib/utils'
import { formatSourceWithPage } from '@/lib/citation-preview'

// ===================
// Types
// ===================

interface FlatCriterionRow {
  id: string
  /** The store object this row was flattened from — what the drawer reads. */
  criterion: ExtractedCriterion
  title: string
  description: string
  weightPercentage: number | null
  weightPoints: number | null
  sourceQuote: string
  sourceDocument: string
  sourcePage: number
  sourceDocumentId: string | null
  sourceHighlightData: SourceHighlightData | null
  extractionSource: 'ai' | 'manual'
  depth: number
  parentNumber: string
  number: string
  hasChildren: boolean
  isLastChild: boolean
  parentId: string | null
}

interface EvaluationCriteriaExtractionProps {
  bidId: string
  readOnly?: boolean
}

// ===================
// Helpers
// ===================

/**
 * A criterion the backend has never seen carries no id, so `flattenCriteria`
 * mints a positional `gen-` one to keep the table keyed. That id names a place
 * in the outline, not a row anyone can comment on — there is nothing to open a
 * drawer against and nothing to count threads for.
 */
function isSavedRowId(id: string | null | undefined): id is string {
  return !!id && !id.startsWith('gen-')
}

function flattenCriteria(
  criteria: ExtractedCriterion[],
  collapsedIds: Set<string>,
  parentNumber: string = '',
  depth: number = 0,
  parentId: string | null = null
): FlatCriterionRow[] {
  const rows: FlatCriterionRow[] = []

  criteria.forEach((criterion, index) => {
    const number = parentNumber
      ? `${parentNumber}.${index + 1}`
      : `${index + 1}`
    const id = criterion.id ?? `gen-${number}`
    const hasChildren = (criterion.subCriteria?.length ?? 0) > 0
    const isLastChild = index === criteria.length - 1

    rows.push({
      id,
      criterion,
      title: criterion.title,
      description: criterion.description,
      weightPercentage: criterion.weightPercentage,
      weightPoints: criterion.weightPoints,
      sourceQuote: criterion.sourceQuote,
      sourceDocument: criterion.sourceDocument,
      sourcePage: criterion.sourcePage,
      sourceDocumentId: criterion.sourceDocumentId ?? null,
      sourceHighlightData: criterion.sourceHighlightData ?? null,
      extractionSource: criterion.extractionSource ?? 'ai',
      depth,
      parentNumber,
      number,
      hasChildren,
      isLastChild,
      parentId,
    })

    // Only flatten children if not collapsed
    if (hasChildren && !collapsedIds.has(id)) {
      rows.push(
        ...flattenCriteria(criterion.subCriteria ?? [], collapsedIds, number, depth + 1, id)
      )
    }
  })

  return rows
}

// ===================
// Component
// ===================

export function EvaluationCriteriaExtraction({ bidId, readOnly }: EvaluationCriteriaExtractionProps) {
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set())
  const [showAddRow, setShowAddRow] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  /** The row whose details the drawer is showing — the id, not the row object. */
  const [activeItemId, setActiveItemId] = useState<string | null>(null)

  const [bulkDelete, setBulkDelete] = useState<{ ids: string[]; aiCount: number } | null>(null)
  const [pendingClear, setPendingClear] = useState<(() => void) | null>(null)

  // Store
  const categoryState = useExtractionStore((s) => s.categories.evaluation_criteria)
  const addManualItem = useExtractionStore((s) => s.addManualItem)
  const deleteItem = useExtractionStore((s) => s.deleteItem)
  const bulkDeleteItems = useExtractionStore((s) => s.bulkDeleteItems)

  // Criterion threads: one bid-wide count fetch feeds every row badge.
  const countsByItemId = useCommentsStore((s) => s.countsByItemId)
  const loadRequirementCounts = useCommentsStore((s) => s.loadRequirementCounts)

  const items = categoryState.items as ExtractedCriterion[]

  // Flatten hierarchy. Response coverage is intentionally NOT surfaced here —
  // it's a late-stage signal that belongs in the Compliance Checklist and the
  // in-context Brief/Plan (scoped-brief §12.1). Analysis shows criterion ·
  // weight · hierarchy.
  const flatRows = useMemo(
    () => flattenCriteria(items, collapsedIds),
    [items, collapsedIds]
  )

  /**
   * The same flatten with nothing collapsed — every criterion in the tender,
   * whatever the user has folded away.
   *
   * Two callers, and they agree on the principle: collapsing a subtree is a
   * VIEW choice, so it must not change scope. The export refuses to drop the
   * rows, and the `?item=` link must be able to reach one.
   *
   * Deliberately NOT the stats-bar count — see the stats bar for why that one
   * counts roots.
   */
  const expandedRows = useMemo(() => flattenCriteria(items, new Set()), [items])

  /**
   * The drawer is driven off the id, not off a captured row: the store hands
   * back a fresh array on every patch, so holding the object would freeze the
   * drawer on a stale copy. Looked up in `flatRows` rather than the tree
   * because the drawer head also wants the `1.2`-style outline number, which
   * only the flatten knows.
   */
  const activeRow = useMemo(
    () => flatRows.find((r) => r.id === activeItemId) ?? null,
    [flatRows, activeItemId]
  )

  // A criterion can leave under the drawer — deleted from the row menu, dropped
  // by a re-extract, or hidden by collapsing its parent. Without this the
  // drawer would sit on nothing.
  useEffect(() => {
    if (activeItemId != null && activeRow == null) setActiveItemId(null)
  }, [activeItemId, activeRow])

  const closeDrawer = useCallback(() => setActiveItemId(null), [])

  /**
   * The activation guard. `ExtractionTable` offers every row, so the host is
   * where an unsaved `gen-` row is turned away — `null` still passes, since it
   * is how clicking the active row again closes the drawer.
   */
  const handleRowActivate = useCallback((rowId: string | null) => {
    if (rowId !== null && !isSavedRowId(rowId)) return
    setActiveItemId(rowId)
  }, [])

  /**
   * Thread counts, once the table actually has rows. Gated on `populated` for
   * the same reason the `?item=` link is: firing it against a still-loading
   * category spends a bid-wide query on a table with nothing to badge.
   */
  const isPopulated = categoryState.state === 'populated'
  useEffect(() => {
    if (!isPopulated) return
    void loadRequirementCounts(bidId, 'criterion')
  }, [bidId, isPopulated, loadRequirementCounts])

  /**
   * And again when the drawer closes — every close path, which is why this is a
   * transition effect rather than a line in `closeDrawer`: clicking the active
   * row again, Escape, and the row disappearing all close it too. This catches
   * the comment someone ELSE left while the drawer was open.
   */
  const drawerWasOpenRef = useRef(false)
  useEffect(() => {
    if (activeItemId !== null) {
      drawerWasOpenRef.current = true
      return
    }
    if (!drawerWasOpenRef.current) return
    drawerWasOpenRef.current = false
    void loadRequirementCounts(bidId, 'criterion')
  }, [activeItemId, bidId, loadRequirementCounts])

  // Toggle collapse
  const toggleCollapse = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  // Stats
  const weightSummary = useMemo(() => summariseCriteriaWeights(items), [items])

  /**
   * `?item=<criterionId>` — the criterion-level deep link a comment
   * notification carries.
   *
   * It names ONE row, so it cannot rely on the view it lands in: a sub-criterion
   * whose parent the user last left collapsed is off screen entirely. This
   * table carries no persistent filters, so widening it means expanding
   * everything rather than clearing a filter set — and only when the row is
   * actually hidden. An arriving link must not fold open a tree the user shaped
   * for a reason it did not need.
   *
   * Two things this shape is load-bearing about:
   *
   * - It waits for `populated`. Spending the link against a still-loading
   *   category would toast "no longer in this list" about a criterion that
   *   simply has not arrived, and spend the link on the way past.
   * - The one-shot ref is what stops the drawer re-opening: the effect re-runs
   *   on every `items` identity change (the store hands back a fresh array on
   *   every patch), and without it closing the drawer would immediately undo
   *   itself.
   *
   * The collapse reset is reachable because `item` arrives by NAVIGATION as
   * well as at mount: this effect re-runs on any later `searchParams` change,
   * and the ref is only spent once a param is actually present. A user sitting
   * on this tab folds a parent away, then follows a criterion comment
   * notification — the param lands on the already-mounted table with the target
   * hidden under the subtree they folded earlier.
   */
  const itemParamConsumedRef = useRef(false)
  useEffect(() => {
    if (itemParamConsumedRef.current) return
    const itemParam = searchParams.get('item')
    if (!itemParam) return
    if (categoryState.state !== 'populated') return
    itemParamConsumedRef.current = true

    // Searched in the fully expanded flatten, so a linked sub-criterion is
    // found whether or not its parent is currently collapsed.
    const target = expandedRows.find((r) => r.id === itemParam)
    if (!target) {
      toast.info('That criterion is no longer in this list')
    } else {
      // Only when the row is genuinely off screen.
      if (!flatRows.some((r) => r.id === itemParam)) setCollapsedIds(new Set())
      setActiveItemId(itemParam)
      // One frame later the expanded table has committed, so the row exists.
      requestAnimationFrame(() => {
        document
          .querySelector(`[data-row-id="${CSS.escape(itemParam)}"]`)
          ?.scrollIntoView({ block: 'center' })
      })
    }

    // Spent on both paths — a link left in the URL re-toasts, and survives a
    // reload to re-open a drawer the user has since closed. Only `item` goes:
    // `view=` is the Analysis sub-tab's own param.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('item')
        return next
      },
      { replace: true }
    )
  }, [searchParams, categoryState.state, expandedRows, flatRows, setSearchParams])

  // Columns
  const columns = useMemo<ColumnDef<FlatCriterionRow, unknown>[]>(
    () => [
      {
        id: 'number',
        header: '#',
        size: 40,
        cell: ({ row }) => {
          const { depth, number, hasChildren } = row.original
          const isCollapsed = collapsedIds.has(row.original.id)
          return (
            <div className="flex items-center" style={{ paddingLeft: `${depth * 16}px` }}>
              {hasChildren && (
                <button
                  // The row itself activates the drawer. Both the stop and the
                  // marker are needed: the marker is what `ExtractionTable`'s
                  // interactive-ancestor check looks for, and the stop keeps a
                  // future handler from seeing the click at all.
                  data-no-row-activate
                  aria-label={isCollapsed ? 'Expand sub-criteria' : 'Collapse sub-criteria'}
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleCollapse(row.original.id)
                  }}
                  className="mr-1 p-0.5 hover:bg-accent rounded"
                >
                  <ChevronRight
                    className={cn(
                      'h-3.5 w-3.5 transition-transform text-muted-foreground',
                      !isCollapsed && 'rotate-90'
                    )}
                  />
                </button>
              )}
              {!hasChildren && depth > 0 && (
                <span className="inline-block w-[18px]" />
              )}
              <span className="text-meta">
                {number}
              </span>
            </div>
          )
        },
      },
      {
        accessorKey: 'title',
        header: 'Criterion',
        size: 220,
        cell: ({ row }) => (
          <span className="text-sm">
            {normalizeTitle(row.original.title)}
          </span>
        ),
      },
      {
        id: 'weight',
        header: 'Weight',
        size: 80,
        cell: ({ row }) => {
          const { weightPercentage, weightPoints } = row.original
          if (weightPercentage == null && weightPoints == null) {
            return <span className="text-meta">—</span>
          }
          const parts: string[] = []
          if (weightPercentage != null) parts.push(`${weightPercentage}%`)
          if (weightPoints != null) parts.push(`${weightPoints} pts`)
          return <span className="text-xs font-medium">{parts.join(' / ')}</span>
        },
      },
      {
        id: 'discussion',
        header: 'Discussion',
        size: 60,
        // Deliberately NOT marked `data-no-row-activate`: this cell is a
        // signpost, not a control. Clicking it activates the row like any
        // other, and the drawer is where the thread actually lives.
        cell: ({ row }) => {
          const itemId = row.original.id
          // Nothing to count against a row the backend has never seen.
          if (!isSavedRowId(itemId)) return null
          const counts = countsByItemId[itemId]
          const total = counts?.total ?? 0
          const unread =
            !!counts &&
            counts.total > 0 &&
            (counts.watermark === null || counts.lastActivityAt > counts.watermark)
          // An icon-only cell says nothing to a screen reader, and the unread
          // dot is otherwise carried by colour alone.
          const label =
            total === 0
              ? 'No discussion'
              : `${total} comment${total === 1 ? '' : 's'}${unread ? ', unread' : ''}`
          return (
            <span
              aria-label={label}
              title={label}
              className={cn(
                'inline-flex items-center gap-1 text-sm',
                total === 0 ? 'text-subtle' : 'text-muted-foreground'
              )}
            >
              <MessageSquare className="h-4 w-4" />
              {total > 0 && total}
              {unread && (
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand))]" />
              )}
            </span>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        size: 40,
        cell: ({ row }) => (
          <RowActionsMenu
            source={row.original.extractionSource}
            sourceQuote={row.original.sourceQuote}
            {...(!readOnly && row.original.extractionSource === 'manual' && row.original.id
              ? { onDelete: () => void deleteItem(bidId, 'evaluation_criteria', row.original.id) }
              : {})}
          />
        ),
      },
    ],
    [bidId, collapsedIds, toggleCollapse, deleteItem, readOnly, countsByItemId]
  )

  // Export
  const handleExport = useCallback(() => {
    // `expandedRows`, never `flatRows`: a collapsed subtree is a view choice,
    // and an export that dropped those rows would hand out an incomplete
    // criteria list. `Note` is gone — the thread replaced it, and a frozen note
    // string would be stale data on an audit artefact.
    const exportData = expandedRows.map((row) => ({
      '#': row.number,
      Criterion: normalizeTitle(row.title),
      Description: row.description || '',
      'Weight (%)': row.weightPercentage ?? '',
      'Weight (pts)': row.weightPoints ?? '',
      Discussion: (() => {
        const counts = isSavedRowId(row.id) ? countsByItemId[row.id] : undefined
        if (!counts || counts.total === 0) return ''
        return `${counts.total} comment${counts.total === 1 ? '' : 's'} (${counts.open} open)`
      })(),
      Source: row.sourceDocument ? formatSourceWithPage(row.sourceDocument, row.sourcePage) : '',
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Evaluation Criteria')
    XLSX.writeFile(
      workbook,
      `evaluation-criteria-${new Date().toISOString().split('T')[0]}.xlsx`
    )
  }, [expandedRows, countsByItemId])

  // Add manual item
  const handleAddManualItem = useCallback(
    async (values: Record<string, string | boolean>) => {
      const asString = (k: string) => {
        const v = values[k]
        return typeof v === 'string' && v ? v : null
      }
      const weightRaw = asString('weightPercentage')
      const weightNum = weightRaw && !isNaN(parseFloat(weightRaw)) ? parseFloat(weightRaw) : null
      // No persistent filter groups on this table, so a saved criterion is
      // always on screen — `null` visibility, never the "hidden" message.
      await addItemWithFeedback(
        'Criterion',
        () =>
          addManualItem(bidId, 'evaluation_criteria', {
            title: asString('title') ?? '',
            description: asString('description') ?? '',
            weightPercentage: weightNum,
            weightPoints: null,
            sourceQuote: '',
            sourceDocument: '',
            sourcePage: 0,
            subCriteria: [],
            extractionSource: 'manual',
          }),
        null
      )
    },
    [bidId, addManualItem]
  )

  // No note field: the thread in the drawer replaced it.
  const modalFields: ModalFieldConfig[] = useMemo(() => [
    { key: 'title', label: 'Criterion', type: 'text', required: true, fullWidth: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'weightPercentage', label: 'Weight %', type: 'text', placeholder: 'e.g. 30' },
  ], [])

  // Global filter. Still reads the description even though the column left the
  // table: it is the text a bid manager remembers a criterion by.
  const globalFilterFn = useCallback((row: FlatCriterionRow, filterValue: string) => {
    const search = filterValue.toLowerCase()
    return (
      row.title.toLowerCase().includes(search) ||
      row.description.toLowerCase().includes(search)
    )
  }, [])

  // Height-bounds the docked aside so its body scrolls internally rather than
  // running the page. Below lg the drawer portals into a Sheet and this
  // wrapper renders empty.
  const detailPanel = activeRow ? (
    <div className="sticky top-4 flex max-h-[calc(100vh-10rem)] min-w-[340px] shrink self-start">
      <CriterionDrawer
        bidId={bidId}
        item={activeRow.criterion}
        itemNumber={activeRow.number}
        onClose={closeDrawer}
        readOnly={readOnly ?? false}
      />
    </div>
  ) : null

  return (
    <>
    <ExtractionTable<FlatCriterionRow>
      bidId={bidId}
      category="evaluation_criteria"
      title="Evaluation Criteria"
      description="Extract evaluation criteria, weightings, and scoring methodology from tender documents."
      columns={columns}
      data={flatRows}
      readOnly={readOnly ?? false}
      onExport={handleExport}
      activeRowId={activeItemId}
      onRowActivate={handleRowActivate}
      {...(!readOnly ? { onAddManualItem: () => setShowAddRow(true) } : {})}
      enableSelection={!readOnly}
      renderBulkActions={(rows, clear) => {
        const ids = rows.map((r) => r.id).filter(isSavedRowId)
        const aiCount = rows.filter((r) => r.extractionSource !== 'manual').length
        return (
          <BulkActionsBar count={rows.length} onClear={clear}>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-background hover:bg-destructive hover:text-destructive-foreground gap-1"
              onClick={() => {
                setBulkDelete({ ids, aiCount })
                setPendingClear(() => clear)
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </BulkActionsBar>
        )
      }}
      globalFilterFn={globalFilterFn}
      statsBar={
        <div className="flex items-center gap-2 text-meta">
          {/* ROOT criteria, counted the same way three other things count
              them: the Overview tile (`EvalCriteriaSummary`), the backend
              weight roll-up, and the weight line sitting right beside this
              one. Sub-criteria are shares of their parent, so counting them
              here would put "17 criteria · Weights total 100%" next to an
              Overview tile reading 7 — the 100% being over the 7.

              No "N of M" search form, deliberately: the only count the
              table could offer is scoped to the flattened rows, and mixing
              that numerator with a root denominator reads as nonsense
              ("5 of 2 criteria"). Not worth a second counting basis. This
              also means folding a subtree cannot move the number. */}
          <span>
            {items.length} {items.length === 1 ? 'criterion' : 'criteria'}
          </span>
          {weightSummary.tone !== 'none' && (
            <>
              <span>·</span>
              <span
                className={
                  weightSummary.tone === 'warn'
                    ? 'text-[hsl(var(--status-warning))]'
                    : 'text-muted-foreground'
                }
              >
                {weightSummary.message}
              </span>
            </>
          )}
        </div>
      }
      detailPanel={detailPanel}
    />
    <AddItemModal
      open={showAddRow}
      onOpenChange={setShowAddRow}
      title="Add evaluation criterion"
      description="Add a manual evaluation criterion to track alongside AI-extracted criteria."
      fields={modalFields}
      onSubmit={handleAddManualItem}
    />
    <BulkConfirmDelete
      open={bulkDelete !== null}
      onOpenChange={(open) => {
        if (!open) {
          setBulkDelete(null)
          setPendingClear(null)
        }
      }}
      count={bulkDelete?.ids.length ?? 0}
      aiCount={bulkDelete?.aiCount ?? 0}
      itemNoun="criterion"
      onConfirm={() => {
        void (async () => {
          if (!bulkDelete) return
          await bulkDeleteItems(bidId, 'evaluation_criteria', bulkDelete.ids)
          pendingClear?.()
          setBulkDelete(null)
          setPendingClear(null)
        })()
      }}
    />
    </>
  )
}
